import { gcm } from '@noble/ciphers/aes.js';
import { pbkdf2Async } from '@noble/hashes/pbkdf2.js';
import { sha256 } from '@noble/hashes/sha2.js';

/** Encode binary as standard base64. */
export function bytesToBase64(b: Uint8Array): string {
  let s = '';
  b.forEach((x) => {
    s += String.fromCharCode(x);
  });
  return btoa(s);
}

/** Decode hex (even length) or base64-ish string to bytes. */
export function decodeFlexibleInput(text: string): Uint8Array {
  const t = text.trim().replace(/\s+/g, '');
  if (/^[0-9a-fA-F]+$/.test(t) && t.length % 2 === 0) {
    const out = new Uint8Array(t.length / 2);
    for (let i = 0; i < out.length; i++) {
      out[i] = Number.parseInt(t.slice(i * 2, i * 2 + 2), 16);
    }
    return out;
  }
  const bin = atob(t);
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

const PBKDF2_ITERS = 210_000;

/**
 * Encrypt arbitrary bytes with the payment password (PBKDF2-SHA256 + AES-256-GCM).
 * Pure JavaScript (@noble/*) — works on http://LAN IP (no Web Crypto `subtle` required).
 * Output is base64(salt16 || iv12 || ciphertext) matching the previous on-wire format.
 */
export async function encryptBytesWithPaymentPassword(password: string, plain: Uint8Array): Promise<string> {
  if (typeof globalThis.crypto?.getRandomValues !== 'function') {
    throw new Error('CRYPTO_RANDOM_UNAVAILABLE');
  }
  const salt = new Uint8Array(16);
  globalThis.crypto.getRandomValues(salt);
  const pw = new TextEncoder().encode(password);
  const keyBytes = await pbkdf2Async(sha256, pw, salt, { c: PBKDF2_ITERS, dkLen: 32 });
  const iv = new Uint8Array(12);
  globalThis.crypto.getRandomValues(iv);
  const aes = gcm(keyBytes, iv);
  const ct = aes.encrypt(plain);
  const out = new Uint8Array(salt.length + iv.length + ct.length);
  out.set(salt, 0);
  out.set(iv, salt.length);
  out.set(ct, salt.length + iv.length);
  return bytesToBase64(out);
}

/**
 * Decrypt base64(salt16 || iv12 || ciphertext) produced by encryptBytesWithPaymentPassword.
 */
export async function decryptBytesWithPaymentPassword(password: string, encryptedB64: string): Promise<Uint8Array> {
  const all = decodeFlexibleInput(encryptedB64);
  if (all.length < 16 + 12 + 16) {
    throw new Error('CIPHERTEXT_TOO_SHORT');
  }
  const salt = all.slice(0, 16);
  const iv = all.slice(16, 28);
  const ct = all.slice(28);
  const pw = new TextEncoder().encode(password);
  const keyBytes = await pbkdf2Async(sha256, pw, salt, { c: PBKDF2_ITERS, dkLen: 32 });
  const aes = gcm(keyBytes, iv);
  return aes.decrypt(ct);
}
